import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import type { T2000 } from '@t2000/sdk';
import { errorResult } from '../errors.js';

// [v4.0 Phase B — 2026-05-26] MCP read surface mirrors the v4 CLI:
//   t2 balance | t2 wallet address | t2 fund | t2 history | t2 services
// Deleted in S.332 (Day 5 bulk delete) and reflected here in S.336:
//   t2000_overview / t2000_positions / t2000_rates / t2000_all_rates /
//   t2000_health / t2000_earnings / t2000_fund_status / t2000_pending_rewards /
//   t2000_deposit_info / t2000_contacts
// — all DeFi / contact-store surface that no longer exists on the agent
// per the agent-wallet greenfield pivot (audric.ai owns DeFi; SuiNS owns
// names).

export function registerReadTools(server: McpServer, agent: T2000): void {
  server.tool(
    't2000_balance',
    "Get the agent's full wallet balance — spendable stablecoins (USDC + USDsui), the SUI gas holding, and any other tokens held (returned in `tokens[]`, amount-only since arbitrary tokens have no USD price oracle). `totalUsd` counts priced holdings only (stables + SUI). v4 wallet is payments-only; for savings positions / lending APYs see audric.ai.",
    {},
    async () => {
      try {
        const result = await agent.balance();
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      } catch (err) {
        return errorResult(err);
      }
    },
  );

  server.tool(
    't2000_address',
    "Get the agent's Sui wallet address. Same value as the first line of `t2 fund`.",
    {},
    async () => {
      try {
        const address = agent.address();
        return { content: [{ type: 'text', text: JSON.stringify({ address }) }] };
      } catch (err) {
        return errorResult(err);
      }
    },
  );

  server.tool(
    't2000_receive',
    'Generate a payment request — returns wallet address, Payment Kit URI (sui:pay?…), nonce, and optional amount/memo. The URI is scannable by any Sui wallet. Use when the user wants to receive a payment, create a payment request, or share their address for receiving funds. Mirrors `t2 fund`.',
    {
      amount: z.number().optional().describe('Amount to request (omit for open amount)'),
      currency: z.string().optional().describe('Currency symbol (default: USDC)'),
      memo: z.string().optional().describe('Payment note'),
      label: z.string().optional().describe('Description for the request'),
    },
    async ({ amount, currency, memo, label }) => {
      try {
        const result = agent.receive({ amount, currency, memo, label });
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      } catch (err) {
        return errorResult(err);
      }
    },
  );

  server.tool(
    't2000_history',
    'View recent on-chain activity — sends, swaps, x402 (paid API) payments. Each entry includes a transaction digest viewable on Suiscan (https://suiscan.xyz/mainnet/tx/{digest}). Mirrors `t2 history`.',
    { limit: z.number().optional().describe('Number of transactions to return (default: 20)') },
    async ({ limit }) => {
      try {
        const result = await agent.history({ limit });
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      } catch (err) {
        return errorResult(err);
      }
    },
  );

  server.tool(
    't2000_agents',
    `Look up agents in the t2000 AGENT DIRECTORY (t2000.ai) — registered on-chain Agent IDs. Distinct from t2000_services (what agents SELL): these are the AGENTS themselves, with on-chain identity.

No address → the registered-agent list (filter with category/limit). With an address → the full identity profile (name, owner, links, on-chain record).`,
    {
      address: z.string().optional().describe("An agent's Sui address for the full listing (omit to list)"),
      category: z
        .string()
        .optional()
        .describe('Filter the list: ai-models | data-feeds | finance | research | dev-tools | creative | other'),
      limit: z.number().optional().describe('Max listings to return (default 30)'),
    },
    async ({ address, category, limit }) => {
      try {
        if (address) {
          const res = await fetch(`https://api.t2000.ai/v1/agents/${address}`);
          if (!res.ok) throw new Error(`Agent lookup failed (${res.status})`);
          return { content: [{ type: 'text', text: JSON.stringify(await res.json()) }] };
        }
        const res = await fetch('https://api.t2000.ai/v1/agents?limit=100');
        if (!res.ok) throw new Error(`Directory lookup failed (${res.status})`);
        const data = (await res.json()) as {
          total?: number;
          agents?: { active?: boolean; service?: string | null; priceUsdc?: string | null; category?: string | null }[];
        };
        let agents = (data.agents ?? []).filter((a) => a.active !== false);
        if (category) {
          const c = category.trim().toLowerCase();
          agents = agents.filter((a) => a.category?.toLowerCase() === c);
        }
        agents = agents.slice(0, Math.min(limit ?? 30, 100));
        return {
          content: [{ type: 'text', text: JSON.stringify({ total: data.total, shown: agents.length, agents }) }],
        };
      } catch (err) {
        return errorResult(err);
      }
    },
  );
}
